import { useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { useRoles } from '../context/RolesContext'
import { ToastContext } from '../../../context/toastContextCore'
import { Sparkline } from '../../Common/Sparkline'
import { EmptyHubState } from '../../Common/EmptyHubState'
import { OwnerTable, type ColumnDef } from '../kit/OwnerTable'
import { OwnerCard } from '../kit/OwnerCard'
import {
  getClientOutreachConfig,
  updateClientOutreachConfig,
  listClientOutreachWorkspaces,
  updateClientOutreachWorkspace,
  listClientOutreachStats,
  type ClientOutreachConfig,
  type ClientOutreachWorkspaceItem,
  type ClientOutreachWorkspaceStatsItem,
  type ClientOutreachSummary,
} from '../../../services/ownerClientOutreachService'
import './ownerClientOutreach.css'

export function OwnerClientOutreachPanel(): ReactNode {
  const { t } = useNls()
  const { role } = useRoles()
  const isFounder = role === 'founder'
  const toast = useContext(ToastContext)

  // Config State
  const [config, setConfig] = useState<ClientOutreachConfig | null>(null)
  const [dailyCapInput, setDailyCapInput] = useState<number>(100)
  const [isSavingCap, setIsSavingCap] = useState(false)
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false)
  const [confirmDisableText, setConfirmDisableText] = useState('')
  const [isUpdatingKillSwitch, setIsUpdatingKillSwitch] = useState(false)

  // Stats State
  const [statsWorkspaces, setStatsWorkspaces] = useState<ClientOutreachWorkspaceStatsItem[]>([])
  const [isStatsLoading, setIsStatsLoading] = useState(false)
  const [statsCursorStack, setStatsCursorStack] = useState<Array<string | null>>([null])
  const [statsPageIndex, setStatsPageIndex] = useState(0)
  const [statsNextCursor, setStatsNextCursor] = useState<string | null>(null)
  const [isStatsTruncated, setIsStatsTruncated] = useState(false)
  const [isStatsPartial, setIsStatsPartial] = useState(false)
  const [statsError, setStatsError] = useState(false)
  const [summary, setSummary] = useState<ClientOutreachSummary | null>(null)

  // Workspaces State
  const [workspaces, setWorkspaces] = useState<ClientOutreachWorkspaceItem[]>([])
  const [isWorkspacesLoading, setIsWorkspacesLoading] = useState(false)
  const [workspacesError, setWorkspacesError] = useState(false)
  const [isWorkspacesPartial, setIsWorkspacesPartial] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null])
  const [pageIndex, setPageIndex] = useState(0)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [workspaceCapInputs, setWorkspaceCapInputs] = useState<Record<string, number>>({})
  const [workspaceBusyId, setWorkspaceBusyId] = useState<string | null>(null)

  // Load Workspaces
  const loadWorkspaces = useCallback(
    async (cursor: string | null = null, search: string | null = null) => {
      setIsWorkspacesLoading(true)
      setWorkspacesError(false)
      try {
        const res = await listClientOutreachWorkspaces({
          cursor: cursor || undefined,
          search: search?.trim() ? search.trim() : undefined,
        })
        setWorkspaces(res.items || [])
        setNextCursor(res.nextCursor || null)
        setIsWorkspacesPartial(res.partial || false)
        const initialCaps: Record<string, number> = {}
        for (const item of res.items || []) {
          initialCaps[item.id] = item.overrideDailyCap ?? item.effectiveDailyCap
        }
        setWorkspaceCapInputs((prev) => ({ ...initialCaps, ...prev }))
      } catch (err) {
        console.error('Failed to load outreach workspaces', err)
        setWorkspacesError(true)
        toast?.showToast('Failed to load workspaces', 'error')
      } finally {
        setIsWorkspacesLoading(false)
      }
    },
    [toast],
  )

  // Load Stats
  const loadStats = useCallback(async (cursor: string | null = null) => {
    setIsStatsLoading(true)
    setStatsError(false)
    try {
      const res = await listClientOutreachStats({
        days: 30,
        cursor: cursor || undefined,
      })
      setStatsWorkspaces(res.items || [])
      setStatsNextCursor(res.nextCursor || null)
      setIsStatsTruncated(res.truncated || false)
      setIsStatsPartial(res.partial || false)
      setSummary(res.summary || null)
    } catch (err) {
      console.error('Failed to load client outreach stats', err)
      setStatsError(true)
    } finally {
      setIsStatsLoading(false)
    }
  }, [])

  useEffect(() => {
    let active = true

    void getClientOutreachConfig()
      .then((cfg) => {
        if (!active) return
        setConfig(cfg)
        setDailyCapInput(cfg.dailyCapDefault)
      })
      .catch((err) => {
        console.error('Failed to load client outreach config', err)
        toast?.showToast('Failed to load outreach config', 'error')
      })

    void listClientOutreachStats({ days: 30 })
      .then((res) => {
        if (!active) return
        setStatsWorkspaces(res.items || [])
        setStatsNextCursor(res.nextCursor || null)
        setIsStatsTruncated(res.truncated || false)
        setIsStatsPartial(res.partial || false)
        setSummary(res.summary || null)
      })
      .catch((err) => {
        console.error('Failed to load client outreach stats', err)
        if (active) setStatsError(true)
      })

    void listClientOutreachWorkspaces({})
      .then((res) => {
        if (!active) return
        setWorkspaces(res.items || [])
        setNextCursor(res.nextCursor || null)
        setIsWorkspacesPartial(res.partial || false)
        const initialCaps: Record<string, number> = {}
        for (const item of res.items || []) {
          initialCaps[item.id] = item.overrideDailyCap ?? item.effectiveDailyCap
        }
        setWorkspaceCapInputs((prev) => ({ ...initialCaps, ...prev }))
      })
      .catch((err) => {
        console.error('Failed to load outreach workspaces', err)
        if (active) setWorkspacesError(true)
        toast?.showToast('Failed to load workspaces', 'error')
      })

    return () => {
      active = false
    }
  }, [toast])

  // Handlers for Global Config
  const handleSaveDefaultCap = async () => {
    if (!isFounder || dailyCapInput < 0 || dailyCapInput > 10000) return
    setIsSavingCap(true)
    try {
      const updated = await updateClientOutreachConfig({ dailyCapDefault: dailyCapInput })
      setConfig(updated)
      setDailyCapInput(updated.dailyCapDefault)
      toast?.showToast(t('NLSOwnerConsoleCapSaved'), 'success')
    } catch (err) {
      console.error('Failed to update daily cap', err)
      toast?.showToast('Failed to save daily cap', 'error')
    } finally {
      setIsSavingCap(false)
    }
  }

  const handleOpenDisableModal = () => {
    if (!isFounder) return
    setConfirmDisableText('')
    setIsDisableModalOpen(true)
  }

  const handleConfirmDisable = async () => {
    if (!isFounder || confirmDisableText !== 'DISABLE') return
    setIsUpdatingKillSwitch(true)
    try {
      const updated = await updateClientOutreachConfig({
        disabled: true,
        confirm: 'DISABLE',
      })
      setConfig(updated)
      setIsDisableModalOpen(false)
      toast?.showToast('Client outreach disabled globally', 'warning')
    } catch (err) {
      console.error('Failed to disable client outreach', err)
      toast?.showToast('Failed to disable client outreach', 'error')
    } finally {
      setIsUpdatingKillSwitch(false)
    }
  }

  const handleEnableKillSwitch = async () => {
    if (!isFounder) return
    setIsUpdatingKillSwitch(true)
    try {
      const updated = await updateClientOutreachConfig({
        disabled: false,
      })
      setConfig(updated)
      toast?.showToast('Client outreach enabled globally', 'success')
    } catch (err) {
      console.error('Failed to enable client outreach', err)
      toast?.showToast('Failed to enable client outreach', 'error')
    } finally {
      setIsUpdatingKillSwitch(false)
    }
  }

  // Handlers for Workspaces
  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    setCursorStack([null])
    setPageIndex(0)
    void loadWorkspaces(null, searchQuery)
  }

  const handlePrevPage = () => {
    if (pageIndex > 0) {
      const targetIndex = pageIndex - 1
      setPageIndex(targetIndex)
      void loadWorkspaces(cursorStack[targetIndex], searchQuery)
    }
  }

  const handleNextPage = () => {
    if (nextCursor) {
      const targetIndex = pageIndex + 1
      const newStack = [...cursorStack.slice(0, targetIndex), nextCursor]
      setCursorStack(newStack)
      setPageIndex(targetIndex)
      void loadWorkspaces(nextCursor, searchQuery)
    }
  }

  const handleStatsPrevPage = () => {
    if (statsPageIndex > 0) {
      const targetIndex = statsPageIndex - 1
      setStatsPageIndex(targetIndex)
      void loadStats(statsCursorStack[targetIndex])
    }
  }

  const handleStatsNextPage = () => {
    if (statsNextCursor) {
      const targetIndex = statsPageIndex + 1
      const newStack = [...statsCursorStack.slice(0, targetIndex), statsNextCursor]
      setStatsCursorStack(newStack)
      setStatsPageIndex(targetIndex)
      void loadStats(statsNextCursor)
    }
  }

  const handleSetWorkspaceCap = async (wsId: string) => {
    if (!isFounder) return
    const targetCap = workspaceCapInputs[wsId]
    if (typeof targetCap !== 'number' || targetCap < 0 || targetCap > 10000) return
    setWorkspaceBusyId(wsId)
    try {
      const res = await updateClientOutreachWorkspace({
        workspaceId: wsId,
        dailyCap: targetCap,
      })
      setWorkspaces((prev) =>
        prev.map((ws) =>
          ws.id === wsId
            ? {
                ...ws,
                overrideDailyCap: res.dailyCap,
                effectiveDailyCap: res.dailyCap ?? (config?.dailyCapDefault || 100),
              }
            : ws,
        ),
      )
      toast?.showToast('Workspace daily cap updated', 'success')
    } catch (err) {
      console.error('Failed to set workspace cap', err)
      toast?.showToast('Failed to set workspace cap', 'error')
    } finally {
      setWorkspaceBusyId(null)
    }
  }

  const handleClearWorkspaceOverride = async (wsId: string) => {
    if (!isFounder) return
    setWorkspaceBusyId(wsId)
    try {
      await updateClientOutreachWorkspace({
        workspaceId: wsId,
        dailyCap: null,
      })
      setWorkspaces((prev) =>
        prev.map((ws) =>
          ws.id === wsId
            ? {
                ...ws,
                overrideDailyCap: null,
                effectiveDailyCap: config?.dailyCapDefault || 100,
              }
            : ws,
        ),
      )
      setWorkspaceCapInputs((prev) => ({
        ...prev,
        [wsId]: config?.dailyCapDefault || 100,
      }))
      toast?.showToast('Workspace override restored to default', 'success')
    } catch (err) {
      console.error('Failed to clear workspace override', err)
      toast?.showToast('Failed to clear workspace override', 'error')
    } finally {
      setWorkspaceBusyId(null)
    }
  }

  const handleToggleWorkspaceDisabled = async (wsId: string, currentDisabled: boolean) => {
    if (!isFounder) return
    setWorkspaceBusyId(wsId)
    try {
      const res = await updateClientOutreachWorkspace({
        workspaceId: wsId,
        disabled: !currentDisabled,
      })
      setWorkspaces((prev) =>
        prev.map((ws) => (ws.id === wsId ? { ...ws, disabled: res.disabled } : ws)),
      )
      toast?.showToast(
        res.disabled ? 'Workspace outreach disabled' : 'Workspace outreach enabled',
        'success',
      )
    } catch (err) {
      console.error('Failed to toggle workspace disabled', err)
      toast?.showToast('Failed to update workspace outreach status', 'error')
    } finally {
      setWorkspaceBusyId(null)
    }
  }

  // Derived Stats from summary (all workspaces, last 30 days UTC)
  const totalSent = summary?.totals?.sent ?? 0
  const totalBounced = summary?.totals?.bounced ?? 0
  const totalComplained = summary?.totals?.complained ?? 0
  const totalOptedOut = summary?.totals?.optedOut ?? 0

  const sparklineValues = useMemo(() => {
    return (summary?.days || []).slice(-7).map((d) => d.sent)
  }, [summary])

  // Stats Table Columns (totals per workspace, sparkline from days array)
  const statsColumns: ColumnDef<ClientOutreachWorkspaceStatsItem>[] = useMemo(
    () => [
      {
        key: 'workspace',
        header: t('NLSOwnerConsoleWorkspaceColName'),
        render: (row) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontWeight: 600 }}>{row.name}</span>
            <code style={{ fontSize: 11, color: 'var(--text-secondary, #94a3b8)' }}>{row.workspaceId}</code>
          </div>
        ),
      },
      {
        key: 'totals',
        header: t('NLSOwnerConsoleStatsColTotals'),
        render: (row) => {
          if (row.error) {
            return (
              <span className="ownerOutreachBadge ownerOutreachBadge--disabled">
                {t('NLSOwnerConsoleStatsRowError')}
              </span>
            )
          }
          return (
            <div style={{ display: 'flex', gap: 8, fontSize: 12, flexWrap: 'wrap' }}>
              <span><strong>{row.totals?.sent ?? 0}</strong> {t('NLSOwnerConsoleStatsSent')}</span>
              <span style={{ color: '#ef4444' }}><strong>{row.totals?.bounced ?? 0}</strong> {t('NLSOwnerConsoleStatsBounced')}</span>
              <span style={{ color: '#f59e0b' }}><strong>{row.totals?.complained ?? 0}</strong> {t('NLSOwnerConsoleStatsComplained')}</span>
              <span style={{ color: '#8b5cf6' }}><strong>{row.totals?.optedOut ?? 0}</strong> {t('NLSOwnerConsoleStatsOptedOut')}</span>
            </div>
          )
        },
      },
      {
        key: 'trend',
        header: t('NLSOwnerConsoleStatsColTrend'),
        render: (row) => {
          if (row.error) {
            return <span style={{ fontSize: 11, color: 'var(--text-secondary, #94a3b8)' }}>-</span>
          }
          const values = (row.days || []).map((d) => d.sent)
          return values.length > 0 ? (
            <Sparkline
              values={values}
              ariaLabel={t('NLSOwnerConsoleStatsRowTrendLabel', { name: row.name })}
              height={20}
              barWidth={4}
              gap={2}
            />
          ) : (
            <span style={{ fontSize: 11, color: 'var(--text-secondary, #94a3b8)' }}>-</span>
          )
        },
      },
    ],
    [t],
  )

  // Table Columns
  const columns: ColumnDef<ClientOutreachWorkspaceItem>[] = useMemo(
    () => [
      {
        key: 'workspace',
        header: t('NLSOwnerConsoleWorkspaceColName'),
        render: (row) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontWeight: 600 }}>{row.name}</span>
            <code style={{ fontSize: 11, color: 'var(--text-secondary, #94a3b8)' }}>{row.id}</code>
          </div>
        ),
      },
      {
        key: 'status',
        header: t('NLSOwnerConsoleWorkspaceColStatus'),
        render: (row) => {
          if (row.disabled) {
            return (
              <span className="ownerOutreachBadge ownerOutreachBadge--disabled">
                {t('NLSOwnerConsoleWorkspaceOverrideDisable')}
              </span>
            )
          }
          if (row.clientOutreachEnabled) {
            return (
              <span className="ownerOutreachBadge ownerOutreachBadge--active">
                {t('NLSOwnerConsoleWorkspaceActive')}
              </span>
            )
          }
          return <span className="ownerOutreachBadge">{t('NLSOwnerConsoleWorkspaceModuleDisabled')}</span>
        },
      },
      {
        key: 'cap',
        header: t('NLSOwnerConsoleWorkspaceColCap'),
        render: (row) => (
          <div>
            <span>{row.effectiveDailyCap}</span>
            {row.overrideDailyCap !== null && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 11,
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: 'rgba(59, 130, 246, 0.15)',
                  color: '#3b82f6',
                }}
              >
                {t('NLSOwnerConsoleWorkspaceOverrideBadge')}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'today',
        header: t('NLSOwnerConsoleWorkspaceColToday'),
        render: (row) => {
          if (row.todayError) {
            return (
              <span className="ownerOutreachBadge ownerOutreachBadge--disabled">
                {t('NLSOwnerConsoleWorkspaceTodayError')}
              </span>
            )
          }
          return <span>{row.todayCount !== null ? row.todayCount : '-'}</span>
        },
      },
      {
        key: 'actions',
        header: t('NLSOwnerConsoleWorkspaceColActions'),
        render: (row) => {
          const capVal = workspaceCapInputs[row.id] ?? row.effectiveDailyCap
          const isBusy = workspaceBusyId === row.id
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="number"
                min={0}
                max={10000}
                className="ownerOutreachInput"
                style={{ width: 75, padding: '4px 8px' }}
                value={capVal}
                disabled={!isFounder || isBusy}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10)
                  setWorkspaceCapInputs((prev) => ({
                    ...prev,
                    [row.id]: isNaN(val) ? 0 : val,
                  }))
                }}
              />
              <button
                type="button"
                className="ownerOutreachInput"
                style={{ cursor: isFounder && !isBusy ? 'pointer' : 'not-allowed' }}
                disabled={!isFounder || isBusy}
                onClick={() => handleSetWorkspaceCap(row.id)}
              >
                {t('NLSOwnerConsoleWorkspaceOverrideSet')}
              </button>
              {row.overrideDailyCap !== null && (
                <button
                  type="button"
                  className="ownerOutreachInput"
                  style={{ cursor: isFounder && !isBusy ? 'pointer' : 'not-allowed' }}
                  disabled={!isFounder || isBusy}
                  onClick={() => handleClearWorkspaceOverride(row.id)}
                >
                  {t('NLSOwnerConsoleWorkspaceOverrideClear')}
                </button>
              )}
              <button
                type="button"
                className="ownerOutreachInput"
                style={{
                  cursor: isFounder && !isBusy ? 'pointer' : 'not-allowed',
                  color: row.disabled ? '#10b981' : '#ef4444',
                }}
                disabled={!isFounder || isBusy}
                onClick={() => handleToggleWorkspaceDisabled(row.id, row.disabled)}
              >
                {row.disabled
                  ? t('NLSOwnerConsoleWorkspaceOverrideEnable')
                  : t('NLSOwnerConsoleWorkspaceOverrideDisable')}
              </button>
            </div>
          )
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isFounder, workspaceBusyId, workspaceCapInputs, config?.dailyCapDefault],
  )

  return (
    <div className="ownerClientOutreachPanel" data-testid="owner-client-outreach-panel">
      <div className="ownerClientOutreachHeader">
        <h1 className="ownerClientOutreachTitle">{t('NLSOwnerConsoleClientOutreachTitle')}</h1>
        <p className="ownerClientOutreachSubtitle">{t('NLSOwnerConsoleClientOutreachSubtitle')}</p>
      </div>

      {!isFounder && (
        <div
          role="note"
          style={{
            padding: '10px 14px',
            background: 'rgba(234, 179, 8, 0.12)',
            border: '1px solid rgba(234, 179, 8, 0.3)',
            borderRadius: 8,
            color: '#facc15',
            fontSize: 13,
          }}
        >
          {t('NLSOwnerConsoleFounderOnlyNote')}
        </div>
      )}

      <div className="ownerClientOutreachGrid">
        {/* Global Controls Card */}
        <OwnerCard title={t('NLSOwnerConsoleGlobalControls')}>
          <div className="ownerOutreachControlRow">
            <div className="ownerOutreachControlInfo">
              <span className="ownerOutreachControlLabel">
                {t('NLSOwnerConsoleGlobalKillSwitch')}
              </span>
              <span className="ownerOutreachControlDesc">
                {t('NLSOwnerConsoleGlobalKillSwitchDesc')}
              </span>
              <div style={{ marginTop: 6 }}>
                {config?.disabled ? (
                  <span className="ownerOutreachBadge ownerOutreachBadge--disabled">
                    {t('NLSOwnerConsoleGlobalKillSwitchActive')}
                  </span>
                ) : (
                  <span className="ownerOutreachBadge ownerOutreachBadge--active">
                    {t('NLSOwnerConsoleGlobalKillSwitchInactive')}
                  </span>
                )}
              </div>
            </div>
            <div>
              {config?.disabled ? (
                <button
                  type="button"
                  className="ownerOutreachInput"
                  data-testid="enable-outreach-btn"
                  style={{
                    cursor: isFounder && !isUpdatingKillSwitch ? 'pointer' : 'not-allowed',
                    color: '#10b981',
                    fontWeight: 600,
                  }}
                  disabled={!isFounder || isUpdatingKillSwitch}
                  onClick={handleEnableKillSwitch}
                >
                  {t('NLSOwnerConsoleEnableOutreach')}
                </button>
              ) : (
                <button
                  type="button"
                  className="ownerOutreachInput"
                  data-testid="disable-outreach-btn"
                  style={{
                    cursor: isFounder && !isUpdatingKillSwitch ? 'pointer' : 'not-allowed',
                    color: '#ef4444',
                    fontWeight: 600,
                  }}
                  disabled={!isFounder || isUpdatingKillSwitch}
                  onClick={handleOpenDisableModal}
                >
                  {t('NLSOwnerConsoleDisableOutreach')}
                </button>
              )}
            </div>
          </div>

          <div className="ownerOutreachControlRow">
            <div className="ownerOutreachControlInfo">
              <span className="ownerOutreachControlLabel">
                {t('NLSOwnerConsoleDefaultDailyCap')}
              </span>
              <span className="ownerOutreachControlDesc">
                {t('NLSOwnerConsoleDefaultDailyCapDesc')}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="number"
                min={0}
                max={10000}
                className="ownerOutreachInput"
                style={{ width: 85 }}
                value={dailyCapInput}
                disabled={!isFounder || isSavingCap}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10)
                  setDailyCapInput(isNaN(val) ? 0 : val)
                }}
                data-testid="default-daily-cap-input"
              />
              <button
                type="button"
                className="ownerOutreachInput"
                style={{
                  cursor:
                    isFounder && !isSavingCap && dailyCapInput >= 0 && dailyCapInput <= 10000
                      ? 'pointer'
                      : 'not-allowed',
                  fontWeight: 600,
                }}
                disabled={
                  !isFounder || isSavingCap || dailyCapInput < 0 || dailyCapInput > 10000
                }
                onClick={handleSaveDefaultCap}
                data-testid="save-cap-btn"
              >
                {t('NLSOwnerConsoleSaveCap')}
              </button>
            </div>
          </div>
        </OwnerCard>

        {/* 30-Day Delivery Health Stats Card */}
        <OwnerCard title={t('NLSOwnerConsoleStatsTitle')} maxBodyHeight="none">
          {statsError ? (
            <div style={{ padding: '24px 0' }}>
              <EmptyHubState
                variant="error"
                title={t('NLSOwnerConsoleLoadError')}
                primaryAction={{
                  label: t('NLSOwnerConsoleRetry'),
                  onClick: () => loadStats(statsCursorStack[statsPageIndex]),
                }}
              />
            </div>
          ) : (
            <>
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--text-secondary, #94a3b8)',
                  marginBottom: 12,
                }}
              >
                {t('NLSOwnerConsoleSummaryHeading')}
              </div>

              {isStatsTruncated && (
                <div
                  data-testid="stats-truncated-note"
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 12,
                  }}
                >
                  {t('NLSOwnerConsoleStatsTruncated')}
                </div>
              )}

              {isStatsPartial && (
                <div
                  data-testid="stats-partial-note"
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    background: 'rgba(234, 179, 8, 0.1)',
                    color: '#eab308',
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 12,
                  }}
                >
                  {t('NLSOwnerConsoleStatsPartialError')}
                </div>
              )}

              <div className="ownerOutreachMetricsRow">
                <div className="ownerOutreachMetricCard">
                  <span className="ownerOutreachMetricValue">{totalSent}</span>
                  <span className="ownerOutreachMetricLabel">{t('NLSOwnerConsoleStatsSent')}</span>
                </div>
                <div className="ownerOutreachMetricCard">
                  <span className="ownerOutreachMetricValue" style={{ color: '#ef4444' }}>
                    {totalBounced}
                  </span>
                  <span className="ownerOutreachMetricLabel">{t('NLSOwnerConsoleStatsBounced')}</span>
                </div>
                <div className="ownerOutreachMetricCard">
                  <span className="ownerOutreachMetricValue" style={{ color: '#f59e0b' }}>
                    {totalComplained}
                  </span>
                  <span className="ownerOutreachMetricLabel">
                    {t('NLSOwnerConsoleStatsComplained')}
                  </span>
                </div>
                <div className="ownerOutreachMetricCard">
                  <span className="ownerOutreachMetricValue" style={{ color: '#8b5cf6' }}>
                    {totalOptedOut}
                  </span>
                  <span className="ownerOutreachMetricLabel">{t('NLSOwnerConsoleStatsOptedOut')}</span>
                </div>
              </div>

              <div className="ownerOutreachSparklineArea">
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{t('NLSOwnerConsoleTrendLast7Days')}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary, #94a3b8)' }}>
                    {t('NLSOwnerConsoleTrendLabel')}
                  </span>
                </div>
                {sparklineValues.length > 0 ? (
                  <Sparkline
                    values={sparklineValues}
                    ariaLabel={t('NLSOwnerConsoleTrendLabel')}
                    height={28}
                    barWidth={6}
                    gap={4}
                  />
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--text-secondary, #94a3b8)' }}>
                    {t('NLSOwnerConsoleNoStats')}
                  </span>
                )}
              </div>

              {statsWorkspaces.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <OwnerTable
                    data={statsWorkspaces}
                    columns={statsColumns}
                    viewKey="owner-client-outreach-stats"
                    getRowId={(row) => row.workspaceId}
                  />
                </div>
              )}

              <div className="ownerOutreachPagination" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="ownerOutreachInput"
                  style={{ cursor: statsPageIndex > 0 ? 'pointer' : 'not-allowed' }}
                  disabled={statsPageIndex === 0 || isStatsLoading}
                  onClick={handleStatsPrevPage}
                  data-testid="stats-pagination-prev-btn"
                >
                  {t('NLSOwnerConsolePaginationPrev')}
                </button>
                <span style={{ fontSize: 13, color: 'var(--text-secondary, #94a3b8)', padding: '0 8px' }}>
                  {t('NLSOwnerConsolePaginationPage', { page: statsPageIndex + 1 })}
                </span>
                <button
                  type="button"
                  className="ownerOutreachInput"
                  style={{ cursor: statsNextCursor ? 'pointer' : 'not-allowed' }}
                  disabled={!statsNextCursor || isStatsLoading}
                  onClick={handleStatsNextPage}
                  data-testid="stats-pagination-next-btn"
                >
                  {t('NLSOwnerConsolePaginationNext')}
                </button>
              </div>
            </>
          )}
        </OwnerCard>
      </div>

      {/* Workspaces Section */}
      <div className="ownerOutreachWorkspacesSection">
        <div className="ownerOutreachToolbar">
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
            {t('NLSOwnerConsoleWorkspaces')}
          </h2>
          <form
            onSubmit={handleSearchSubmit}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <input
              type="text"
              placeholder={t('NLSOwnerConsoleSearchWorkspaces')}
              className="ownerOutreachInput"
              style={{ width: 220 }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-workspaces-input"
            />
            <button
              type="submit"
              className="ownerOutreachInput"
              style={{ cursor: 'pointer', fontWeight: 600 }}
              data-testid="search-workspaces-btn"
            >
              {t('NLSOwnerConsoleSearchBtn')}
            </button>
          </form>
        </div>

        {workspacesError ? (
          <div style={{ padding: '24px 0' }}>
            <EmptyHubState
              variant="error"
              title={t('NLSOwnerConsoleLoadError')}
              primaryAction={{
                label: t('NLSOwnerConsoleRetry'),
                onClick: () => loadWorkspaces(cursorStack[pageIndex], searchQuery),
              }}
            />
          </div>
        ) : (
          <>
            {isWorkspacesPartial && (
              <div
                data-testid="workspaces-partial-note"
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  background: 'rgba(234, 179, 8, 0.1)',
                  color: '#eab308',
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 12,
                }}
              >
                {t('NLSOwnerConsoleStatsPartialError')}
              </div>
            )}

            <OwnerTable
              data={workspaces}
              columns={columns}
              viewKey="owner-client-outreach-workspaces"
              getRowId={(row) => row.id}
              emptyState={
                <div style={{ padding: '24px 0', textAlign: 'center', color: '#94a3b8' }}>
                  {t('NLSOwnerConsoleNoWorkspaces')}
                </div>
              }
            />

            <div className="ownerOutreachPagination">
              <button
                type="button"
                className="ownerOutreachInput"
                style={{ cursor: pageIndex > 0 ? 'pointer' : 'not-allowed' }}
                disabled={pageIndex === 0 || isWorkspacesLoading}
                onClick={handlePrevPage}
                data-testid="pagination-prev-btn"
              >
                {t('NLSOwnerConsolePaginationPrev')}
              </button>
              <span style={{ fontSize: 13, color: 'var(--text-secondary, #94a3b8)', padding: '0 8px' }}>
                {t('NLSOwnerConsolePaginationPage', { page: pageIndex + 1 })}
              </span>
              <button
                type="button"
                className="ownerOutreachInput"
                style={{ cursor: nextCursor ? 'pointer' : 'not-allowed' }}
                disabled={!nextCursor || isWorkspacesLoading}
                onClick={handleNextPage}
                data-testid="pagination-next-btn"
              >
                {t('NLSOwnerConsolePaginationNext')}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Confirm Disable Modal */}
      {isDisableModalOpen && (
        <div
          className="ownerConfirmModalOverlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setIsDisableModalOpen(false)}
        >
          <div className="ownerConfirmModalDialog" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0, fontSize: 18, color: '#ef4444' }}>
              {t('NLSOwnerConsoleConfirmDisableTitle')}
            </h3>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary, #94a3b8)' }}>
              {t('NLSOwnerConsoleConfirmDisablePrompt')}
            </p>
            <input
              type="text"
              className="ownerOutreachInput"
              value={confirmDisableText}
              onChange={(e) => setConfirmDisableText(e.target.value)}
              placeholder="DISABLE"
              data-testid="confirm-disable-input"
              autoFocus
            />
            <div className="ownerConfirmModalActions">
              <button
                type="button"
                className="ownerOutreachInput"
                style={{ cursor: 'pointer' }}
                onClick={() => setIsDisableModalOpen(false)}
              >
                {t('NLSOwnerConsoleCancel')}
              </button>
              <button
                type="button"
                className="ownerOutreachInput"
                style={{
                  cursor:
                    confirmDisableText === 'DISABLE' && !isUpdatingKillSwitch
                      ? 'pointer'
                      : 'not-allowed',
                  background: confirmDisableText === 'DISABLE' ? '#ef4444' : undefined,
                  color: confirmDisableText === 'DISABLE' ? '#ffffff' : undefined,
                  fontWeight: 600,
                }}
                disabled={confirmDisableText !== 'DISABLE' || isUpdatingKillSwitch}
                onClick={handleConfirmDisable}
                data-testid="confirm-disable-btn"
              >
                {t('NLSOwnerConsoleConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
